/**
 * React Query mutation factories for CRM operations.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  type EventBatch,
  isAsyncSingleObjectDeleteResponse,
  type SubmitEventsResponse,
} from '../../../types/crm';
import { createCRMObjectClient } from '../core/CRMObjectClient';
import type { EntityTypeName } from '../core/types';
import {
  applyOptimisticBatch,
  type OptimisticBatchContext,
  rollbackOptimisticBatch,
} from '../store/optimisticBatch';
import { crmQueryKeys } from './queries';

/**
 * Hook for submitting events (create/update/delete operations).
 */
export function useSubmitEvents(teamId: number, entityType: EntityTypeName) {
  const queryClient = useQueryClient();
  // Stable reference prevents exhaustive-deps violations if this client
  // is ever captured in a useCallback or useEffect dependency array.
  const client = useMemo(
    () => createCRMObjectClient(teamId, entityType),
    [teamId, entityType]
  );

  return useMutation<SubmitEventsResponse, Error, EventBatch>({
    mutationFn: (batch: EventBatch) => client.submitEvents(batch),
    onSuccess: () => {
      // Invalidate all queries for this entity type to refetch
      queryClient.invalidateQueries({
        queryKey: crmQueryKeys.objects(teamId, entityType),
      });
    },
  });
}

/**
 * Hook for submitting events with optimistic updates.
 *
 * The store write and its seq-guarded rollback live in
 * `store/optimisticBatch` so `useUpdateCrmProperty` — which owns a 409 retry
 * loop and therefore cannot nest this mutation — can share the same
 * implementation.
 */
export function useSubmitEventsOptimistic(
  teamId: number,
  entityType: EntityTypeName,
  onSuccess?: (data: SubmitEventsResponse) => void
) {
  const queryClient = useQueryClient();
  // Stable reference prevents exhaustive-deps violations if this client
  // is ever captured in a useCallback or useEffect dependency array.
  const client = useMemo(
    () => createCRMObjectClient(teamId, entityType),
    [teamId, entityType]
  );

  return useMutation<
    SubmitEventsResponse,
    Error,
    EventBatch,
    OptimisticBatchContext
  >({
    mutationFn: (batch: EventBatch) => client.submitEvents(batch),
    onMutate: async (batch: EventBatch) => {
      // Cancel outgoing refetches so an in-flight response can't land on top
      // of the optimistic write we're about to make.
      await queryClient.cancelQueries({
        queryKey: crmQueryKeys.objects(teamId, entityType),
      });
      return applyOptimisticBatch(batch, teamId);
    },
    onSuccess: data => {
      // A 202 async single-object delete hasn't applied anything server-side
      // yet — the background job is still running. Invalidating here would
      // refetch and rehydrate the optimistically-hidden row before the job
      // finishes. Defer invalidation to the job's terminal handler
      // (SingleObjectDeleteJobHost), which invalidates once the job settles.
      if (!isAsyncSingleObjectDeleteResponse(data)) {
        queryClient.invalidateQueries({
          queryKey: crmQueryKeys.objects(teamId, entityType),
        });
      }
      onSuccess?.(data);
    },
    onError: (_error, _variables, context) => {
      // Roll the optimistic store write back. Context is undefined when
      // onMutate itself threw, in which case there is nothing to undo.
      if (context) {
        rollbackOptimisticBatch(context);
      }
      queryClient.invalidateQueries({
        queryKey: crmQueryKeys.objects(teamId, entityType),
      });
    },
  });
}
